/**
 * Refinanciamiento Externo — migración web de ares2060 (escritorio).
 *
 * Crea un préstamo tipo 'R' sobre un chasis DISPONIBLE del inventario para un
 * cliente que viene financiado de otra financiera. Reglas del escritorio:
 *   - SIEMPRE requiere autorización de un AdmG (el guardar responde 409 con
 *     requiereAutorizacion y aquí se solicita/pollea igual que ares2010).
 *   - El precio tiene PISO en el COSTO del chasis (no el precio de venta).
 *   - Consume los iniciales abonados al chasis (Financia = precio − inicial).
 *   - Fiador obligatorio.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { calcularCuotas, useConfigCuotas, mananaISO, unMesISO } from "lib/calculocuotas";
import CampoCuota from "components/cuotas/CampoCuota";

const FORM_INICIAL = {
  // Cliente
  cedula: "",
  nombres: "",
  apellidos: "",
  direccion: "",
  provincia: "",
  telefono: "",
  celular: "",
  email: "",
  apodo: "",
  edad: "",
  // Fiador
  fiador: "",
  fiadorced: "",
  fiadortel: "",
  fiadordir: "",
  // Vehículo
  chasis: "",
  marca: "",
  modelo: "",
  colore: "",
  precio: "",
  // Financiamiento
  formadepago: "",
  fechaprimeracuota: "",
  interes: "",
  plazo: "",
  comentario: "",
};

const FORMAS_PAGO = ["Meses", "Quincenas", "Semanas", "Dias"];

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Formatea cédula dominicana como 000-0000000-0 (progresivo mientras se escribe).
const formatCedula = (v) => {
  const d = String(v || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 10);
  const p3 = d.slice(10, 11);
  let out = p1;
  if (p2) out += "-" + p2;
  if (p3) out += "-" + p3;
  return out;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function RefinanciamientoExterno({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { redondeo, elegirPrimeraCuota } = useConfigCuotas();

  const [form, setForm] = useState(FORM_INICIAL);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Provincias del tenant.
  const [provincias, setProvincias] = useState([]);
  useEffect(() => {
    let activo = true;
    post("/api/prestamopersonal_config", {}).then(({ ok, data }) => {
      if (!activo) return;
      if (ok) setProvincias(data.provincias || []);
      else
        showAlert({ message: data.Error || "No se pudo cargar la configuración", color: "error" });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Cliente: precarga por cédula (best-effort, si falla se sigue sin datos) ----
  const buscarPorCedula = useCallback(async () => {
    const ced = form.cedula.trim();
    if (ced.length < 11) return;
    try {
      const { ok, data } = await post("/api/ventaproducto_buscarcliente", { cedula: ced });
      if (!ok || !data.solicitud) return;
      const s = data.solicitud;
      setForm((p) => ({
        ...p,
        nombres: s.nombres || p.nombres,
        apellidos: s.apellidos || p.apellidos,
        direccion: s.direccion || p.direccion,
        provincia: s.urb || p.provincia,
        telefono: s.telefono || p.telefono,
        celular: s.celular || p.celular,
        email: s.email || p.email,
        apodo: s.apodo || p.apodo,
        edad: s.edad != null ? String(s.edad) : p.edad,
      }));
    } catch {
      /* la precarga es opcional */
    }
  }, [form.cedula, post]);

  // ---- Vehículo ----
  const [chasisValidado, setChasisValidado] = useState(false);
  const [validandoChasis, setValidandoChasis] = useState(false);
  const [costo, setCosto] = useState(0);
  const [inicial, setInicial] = useState(0);

  const limpiarChasis = () => {
    setChasisValidado(false);
    setCosto(0);
    setInicial(0);
    setForm((p) => ({ ...p, marca: "", modelo: "", colore: "", precio: "" }));
  };

  const validarChasis = async () => {
    const chasis = form.chasis.trim();
    if (!chasis) {
      showAlert({ message: "Indique el chasis a validar", color: "warning" });
      return;
    }
    setValidandoChasis(true);
    try {
      const { ok, data } = await post("/api/refinexterno_validarchasis", { chasis });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo validar el chasis", color: "error" });
        limpiarChasis();
        return;
      }
      const wcosto = toNum(data.costo);
      const winicial = toNum(data.inicial);
      setChasisValidado(true);
      setCosto(wcosto);
      setInicial(winicial);
      setForm((p) => ({
        ...p,
        marca: data.producto?.marca || "",
        modelo: data.producto?.modelo || "",
        colore: data.producto?.colore || "",
        // El precio arranca en el costo (piso del escritorio) pero es editable.
        precio: wcosto ? String(wcosto) : "",
        // Cambiar de chasis invalida el cálculo anterior.
        formadepago: "",
        fechaprimeracuota: "",
        interes: "",
        plazo: "",
      }));
    } finally {
      setValidandoChasis(false);
    }
  };

  // Financia = precio − inicial (solo lectura).
  const financia = useMemo(() => {
    const f = toNum(form.precio) - inicial;
    return chasisValidado && toNum(form.precio) > 0 ? f : 0;
  }, [form.precio, inicial, chasisValidado]);

  // Vista previa local del financiamiento.
  const preview = useMemo(
    () =>
      calcularCuotas(financia, toNum(form.plazo), form.formadepago, toNum(form.interes), redondeo),
    [financia, form.plazo, form.formadepago, form.interes, redondeo]
  );

  // ---- Autorización (mismo contrato que ventaproductos/ares2010) ----
  const [autOpen, setAutOpen] = useState(false);
  const [autMotivo, setAutMotivo] = useState("");
  const [autIdtext, setAutIdtext] = useState("");
  const [autStatus, setAutStatus] = useState(""); // "" | Progreso | Aprobado | Cancelado
  const [autComentario, setAutComentario] = useState("");
  const pollRef = useRef(null);

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => detenerPolling, []);

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    detenerPolling();
    setForm(FORM_INICIAL);
    setChasisValidado(false);
    setCosto(0);
    setInicial(0);
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  const validar = () => {
    if (form.cedula.replace(/\D/g, "").length < 11) {
      showAlert({ message: "La cédula es inválida", color: "warning" });
      return false;
    }
    if (
      !form.fiador.trim() ||
      !form.fiadorced.trim() ||
      !form.fiadortel.trim() ||
      !form.fiadordir.trim()
    ) {
      showAlert({ message: "Los datos del fiador son obligatorios", color: "warning" });
      return false;
    }
    if (!chasisValidado || !form.chasis.trim()) {
      showAlert({ message: "Debe validar el chasis antes de guardar", color: "warning" });
      return false;
    }
    if (toNum(form.precio) < costo) {
      showAlert({
        message: "El precio no puede ser menor al costo definido en el chasis",
        color: "warning",
      });
      return false;
    }
    if (!form.formadepago || toNum(form.plazo) <= 0 || toNum(form.interes) <= 0) {
      showAlert({
        message: "Complete la forma de pago, el interés y el plazo",
        color: "warning",
      });
      return false;
    }
    return true;
  };

  const guardar = async (idautorizacion = "") => {
    if (!validar()) return;
    setGuardando(true);
    try {
      const { ok, status, data } = await post("/api/refinexterno_guardar", {
        cedula: form.cedula.trim(),
        nombres: form.nombres.trim(),
        apellidos: form.apellidos.trim(),
        direccion: form.direccion.trim(),
        provincia: form.provincia,
        telefono: form.telefono.trim(),
        celular: form.celular.trim(),
        email: form.email.trim(),
        apodo: form.apodo.trim(),
        edad: toNum(form.edad),
        fiador: form.fiador.trim(),
        fiadorced: form.fiadorced.trim(),
        fiadortel: form.fiadortel.trim(),
        fiadordir: form.fiadordir.trim(),
        chasis: form.chasis.trim(),
        precio: toNum(form.precio),
        formadepago: form.formadepago,
        fechaprimeracuota: elegirPrimeraCuota ? form.fechaprimeracuota : "",
        interes: toNum(form.interes),
        plazo: toNum(form.plazo),
        comentario: form.comentario.trim(),
        idautorizacion,
      });
      if (ok) {
        showAlert({
          message: `${data.ok || "Refinanciamiento externo registrado"} — Préstamo #${
            data.noprest
          } (cuota RD$ ${fmtMoney(data.valorcuota)})`,
          color: "success",
        });
        limpiar();
        if (onSaved) onSaved();
        return;
      }
      if (status === 409 && data.requiereAutorizacion) {
        setAutMotivo(
          data.Error || "El refinanciamiento externo siempre requiere autorización de un AdmG"
        );
        setAutIdtext("");
        setAutStatus("");
        setAutComentario("");
        setAutOpen(true);
        return;
      }
      showAlert({ message: data.Error || "Error al guardar", color: "error" });
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const solicitarAutorizacion = async () => {
    const { ok, data } = await post("/api/ventaproducto_solicitarautorizacion", {
      descripcion: `Refinanciamiento externo chasis ${form.chasis} cliente ${
        `${form.nombres} ${form.apellidos}`.trim() || form.cedula
      }`,
      tipo: "Aut.Prestamo",
      datos: {
        cedula: form.cedula,
        nombre: form.nombres,
        apellido: form.apellidos,
        edad: form.edad,
        chasis: form.chasis,
        marca: form.marca,
        modelo: form.modelo,
        color: form.colore,
        precio: form.precio,
        inicial: String(inicial),
        financia: financia.toFixed(2),
        plazo: form.plazo,
        formadepago: form.formadepago,
        interes: form.interes,
        valorcuota: preview ? preview.valorcuota.toFixed(2) : "",
        deudatotal: preview ? preview.deudatotal.toFixed(2) : "",
      },
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo solicitar la autorización", color: "error" });
      return;
    }
    setAutIdtext(data.idtext);
    setAutStatus("Progreso");
    detenerPolling();
    pollRef.current = setInterval(async () => {
      const r = await post("/api/ventaproducto_estadoautorizacion", { idtext: data.idtext });
      if (!r.ok) return;
      const st = r.data.status;
      if (st === "Aprobado") {
        detenerPolling();
        setAutStatus("Aprobado");
        // Aprobada: se reintenta el guardar con el id de la autorización.
        guardar(data.idtext);
      } else if (st === "Cancelado") {
        detenerPolling();
        setAutStatus("Cancelado");
        setAutComentario(r.data.comentario || "");
      }
    }, 5000);
  };

  const cancelarAutorizacion = async () => {
    detenerPolling();
    if (autIdtext && autStatus === "Progreso") {
      await post("/api/ventaproducto_cancelarautorizacion", { idtext: autIdtext });
    }
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  const chipInfo = {
    bgcolor: FP.color.surfaceAlt,
    color: FP.color.textSecondary,
    fontWeight: 700,
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Refinanciamiento Externo"
        subtitle="Préstamo sobre un chasis disponible para clientes de otras financieras (ares2060)"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => guardar()}
              disabled={guardando}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Cliente */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.accentBlue}>
            Cliente
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Cédula"
                value={form.cedula}
                onChange={(e) => setForm((p) => ({ ...p, cedula: formatCedula(e.target.value) }))}
                onBlur={buscarPorCedula}
                placeholder="000-0000000-0"
                inputProps={{ maxLength: 13 }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nombres"
                value={form.nombres}
                onChange={setF("nombres")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Apellidos"
                value={form.apellidos}
                onChange={setF("apellidos")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Dirección"
                value={form.direccion}
                onChange={setF("direccion")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={provincias}
                value={form.provincia || null}
                onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || "" }))}
                renderInput={(params) => <TextField {...params} label="Provincia" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Teléfono"
                value={form.telefono}
                onChange={setF("telefono")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Celular"
                value={form.celular}
                onChange={setF("celular")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Email"
                value={form.email}
                onChange={setF("email")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Apodo"
                value={form.apodo}
                onChange={setF("apodo")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <TextField
                fullWidth
                type="number"
                label="Edad"
                value={form.edad}
                onChange={setF("edad")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Fiador */}
        <Box sx={sectionBox}>
          <SectionTitle icon={AssignmentIndOutlinedIcon} color={FP.color.danger}>
            Fiador
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nombre fiador"
                value={form.fiador}
                onChange={setF("fiador")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cédula fiador"
                value={form.fiadorced}
                onChange={(e) =>
                  setForm((p) => ({ ...p, fiadorced: formatCedula(e.target.value) }))
                }
                placeholder="000-0000000-0"
                inputProps={{ maxLength: 13 }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Teléfono fiador"
                value={form.fiadortel}
                onChange={setF("fiadortel")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección fiador"
                value={form.fiadordir}
                onChange={setF("fiadordir")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Vehículo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.info}>
            Vehículo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5} md={4}>
              <TextField
                fullWidth
                label="Chasis"
                value={form.chasis}
                onChange={(e) => {
                  setForm((p) => ({ ...p, chasis: e.target.value }));
                  if (chasisValidado) limpiarChasis();
                }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                fullWidth
                startIcon={
                  validandoChasis ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )
                }
                onClick={validarChasis}
                disabled={validandoChasis || !form.chasis.trim()}
                sx={btnOutline(FP.color.accentBlue)}
              >
                Validar
              </Button>
            </Grid>
            {chasisValidado && (
              <Grid item xs={12} sm={4} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={[form.marca, form.modelo, form.colore].filter(Boolean).join(" · ")}
                    sx={chipInfo}
                  />
                  <Chip label={`Costo: RD$ ${fmtMoney(costo)}`} sx={chipInfo} />
                  <Chip
                    label={`Inicial abonado: RD$ ${fmtMoney(inicial)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                </Stack>
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Precio"
                value={form.precio}
                onChange={setF("precio")}
                disabled={!chasisValidado}
                helperText="Mínimo: costo del chasis"
                sx={fpInputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Financia"
                value={fmtMoney(financia)}
                sx={fpInputSx}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
                helperText="Precio − inicial abonado"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Financiamiento */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Financiamiento
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Forma de pago"
                value={form.formadepago}
                onChange={setF("formadepago")}
                disabled={!chasisValidado}
                sx={fpInputSx}
              >
                {FORMAS_PAGO.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {elegirPrimeraCuota && (
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha primera cuota"
                  value={form.fechaprimeracuota}
                  onChange={setF("fechaprimeracuota")}
                  disabled={!chasisValidado}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: mananaISO(), max: unMesISO() }}
                  sx={fpInputSx}
                  helperText="Vacía = automática; hasta un mes"
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Interés (mensual)"
                value={form.interes}
                onChange={setF("interes")}
                disabled={!chasisValidado}
                sx={fpInputSx}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Plazo (cuotas)"
                value={form.plazo}
                onChange={setF("plazo")}
                disabled={!chasisValidado}
                sx={fpInputSx}
              />
            </Grid>
            {redondeo && (
              <Grid item xs={6} sm={3}>
                <CampoCuota
                  editable={chasisValidado}
                  label="Valor cuota"
                  cuota={preview ? preview.valorcuota : null}
                  financia={financia}
                  plazo={toNum(form.plazo)}
                  formadepago={form.formadepago}
                  onInteres={(interes) => setForm((p) => ({ ...p, interes }))}
                  disabled={!chasisValidado}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "center" }}>
              <Stack spacing={0.4}>
                <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                  Valor cuota:{" "}
                  <b style={{ color: FP.color.textPrimary }}>
                    RD$ {preview ? fmtMoney(preview.valorcuota) : "0.00"}
                  </b>
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                  Deuda total:{" "}
                  <b style={{ color: FP.color.textPrimary }}>
                    RD$ {preview ? fmtMoney(preview.deudatotal) : "0.00"}
                  </b>
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Comentario */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={ChatBubbleOutlineIcon} color={FP.color.navy}>
            Comentario
          </SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Comentario"
            value={form.comentario}
            onChange={setF("comentario")}
            inputProps={{ maxLength: 1000 }}
          />
        </Box>
      </Box>

      {/* Dialog de autorización */}
      <Dialog
        open={autOpen}
        onClose={() => {}}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <GppMaybeOutlinedIcon sx={{ color: FP.color.warning }} />
          Se requiere autorización
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            {autMotivo}
          </Typography>

          {autStatus === "" && (
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textMuted }}>
              Haz clic en &quot;Solicitar autorización&quot; para enviar la petición al
              administrador.
            </Typography>
          )}
          {autStatus === "Progreso" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CircularProgress size={18} />
              <Chip
                label="Esperando autorización..."
                sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
              />
            </Stack>
          )}
          {autStatus === "Aprobado" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CheckCircleOutlineIcon sx={{ color: FP.color.success }} />
              <Typography sx={{ fontSize: "0.9rem", color: FP.color.success, fontWeight: 700 }}>
                Autorización aprobada. Guardando el refinanciamiento…
              </Typography>
            </Stack>
          )}
          {autStatus === "Cancelado" && (
            <Typography sx={{ fontSize: "0.9rem", color: FP.color.danger, fontWeight: 700 }}>
              Autorización declinada{autComentario ? ` — mensaje: ${autComentario}` : ""}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cancelarAutorizacion} sx={btnGhost}>
            Cerrar
          </Button>
          {autStatus === "" && (
            <Button
              disableElevation
              onClick={solicitarAutorizacion}
              sx={btnSolid(FP.color.warning, "#B45309")}
            >
              Solicitar autorización
            </Button>
          )}
          {autStatus === "Aprobado" && (
            <Button
              disableElevation
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={guardando}
              onClick={() => guardar(autIdtext)}
              sx={btnSolid(FP.color.success, FP.color.successDark)}
            >
              Guardar con autorización
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
